/**
 * Shared ProgressDisplay component for upload progress
 * Handles progress bar and status display for both photo and CSV uploads
 */

import React from 'react';
import { Alert, Stack, Group, Text, Progress, Badge, Loader } from '@mantine/core';

export interface ProgressDisplayProps {
  loading: boolean;
  uploadStatus: string;
  uploadProgress: number;
  currentFileStatus: string;
  currentFileIndex: number;
  totalFiles: number;
  currentStage: string;
  detailedStatus: string;
  /**
   * Optional: actual browser->server upload progress (bytes sent) for the POST request.
   * This is the most accurate signal during the network upload phase.
   *
   * When provided, the UI will show a separate "Uploading files" progress bar.
   */
  requestUploadProgress?: number | null;
}

/**
 * ProgressDisplay component for showing upload progress
 * Provides consistent progress display across upload pages
 */
export const ProgressDisplay: React.FC<ProgressDisplayProps> = ({
  loading,
  uploadStatus,
  uploadProgress,
  currentFileStatus,
  currentFileIndex,
  totalFiles,
  currentStage,
  detailedStatus,
  requestUploadProgress = null
}) => {
  if (!loading) return null;

  // If we have request upload progress, treat that as "phase 1" (network upload).
  // This prevents the UX issue where the progress bar appears stuck at 0% while
  // the user is actually uploading a large file over the network.
  const hasRequestUploadProgress =
    typeof requestUploadProgress === 'number' &&
    Number.isFinite(requestUploadProgress) &&
    requestUploadProgress >= 0;

  const clampedRequestUploadProgress = hasRequestUploadProgress
    ? Math.max(0, Math.min(100, Math.round(requestUploadProgress)))
    : null;

  const isRequestUploadInFlight =
    clampedRequestUploadProgress !== null && clampedRequestUploadProgress < 100;

  return (
    <Stack gap="md">
      <Alert
        title={isRequestUploadInFlight ? 'Uploading files…' : 'Upload in progress'}
        color="green"
      >
        <Stack gap="md">
          {/* Phase 1: Actual network upload progress (bytes sent) */}
          {clampedRequestUploadProgress !== null && (
            <div>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Uploading files to server
                </Text>
                <Text size="sm" c="dimmed">
                  {clampedRequestUploadProgress}%
                </Text>
              </Group>
              <Progress
                value={clampedRequestUploadProgress}
                size="lg"
                radius="md"
                color={clampedRequestUploadProgress < 90 ? 'green' : 'teal'}
                animated={isRequestUploadInFlight}
              />
            </div>
          )}

          {/* Progress Bar */}
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>{uploadStatus}</Text>
              <Text size="sm" c="dimmed">{uploadProgress}%</Text>
            </Group>
            <Progress 
              value={uploadProgress} 
              size="lg" 
              radius="md"
              color={uploadProgress < 30 ? "green" : uploadProgress < 60 ? "cyan" : uploadProgress < 90 ? "green" : "teal"}
              animated
            />
          </div>
          
          {/* File Progress */}
          {totalFiles > 1 && (
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm">
                File {currentFileIndex + 1} of {totalFiles}
              </Text>
            </Group>
          )}
          
          {/* Current File Status */}
          {currentFileStatus && (
            <Text size="sm" c="dimmed">
              {currentFileStatus}
            </Text>
          )}
          
          {/* Stage Badge */}
          {currentStage && (
            <Badge 
              variant="light" 
              color={getStageColor(currentStage)}
              size="sm"
            >
              {currentStage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
          )}
          
          {/* Detailed Status */}
          {detailedStatus && detailedStatus !== uploadStatus && (
            <Text size="sm" c="dimmed">
              {detailedStatus}
            </Text>
          )}
        </Stack>
      </Alert>
    </Stack>
  );
};

/**
 * Get color for stage badge based on stage name
 * @param stage - The current stage name
 * @returns Color string for the badge
 */
const getStageColor = (stage: string): string => {
  if (stage.includes('initializing')) return 'green';
  if (stage.includes('restaurant')) return 'cyan';
  if (stage.includes('menu')) return 'teal';
  if (stage.includes('parsing') || stage.includes('csv')) return 'yellow';
  if (stage.includes('processing') || stage.includes('ai')) return 'orange';
  if (stage.includes('saving')) return 'green';
  if (stage.includes('finalizing')) return 'violet';
  return 'gray';
};

export default ProgressDisplay;
